import { Box, Button, HStack, Text, Tooltip } from "@chakra-ui/react";
import {
  File,
  FileCsv,
  FilePdf,
  FileXls,
  MagnifyingGlass,
  Sparkle,
  X,
} from "@phosphor-icons/react";
import FileDownloadButton from "components/FileDownloadButton";
import { getFileType } from "api/common/fileinfo";
import { formatDate } from "utils";
import { SOURCE_STATE } from "utils/foundationSourceRuns";
import AIProgressWidget from "../../AITasks/AIProgressWidget";

const SOURCE_FILE_TYPE_ICONS = { PDF: FilePdf, XLSX: FileXls, XLS: FileXls, CSV: FileCsv };

/**
 * One document in the Sources Added card: what it is, what has been read out of it, and what to do next.
 *
 * The action comes from `describeSourceRun`, which owns the state machine. This component only renders it —
 * so the ordering rules that matter (unsupported outranks unsaved, a cancelled run is not a result) live in
 * one tested place rather than in JSX conditions.
 */
export default function SourceRow({
  fileInfo,
  descriptor,
  matchedCount,
  canEdit,
  taskId,
  isStarting,
  isBlocked,
  onExtract,
  onReview,
  onCancel,
  onRemove,
}) {
  const fileType = getFileType(fileInfo);
  const Icon = SOURCE_FILE_TYPE_ICONS[fileType] || File;
  const { state, action, note } = descriptor;
  // What matters is how much of this document was USABLE — how many of its rows match a datapoint the
  // target structure actually wants — not how many rows the extraction happened to produce. Falls back to
  // the raw count only when there's no structure to match against at all (matchedCount is undefined then,
  // never 0-meaning-"no structure" — buildTargetView would otherwise read as "nothing matched").
  const hasMatchedCount = matchedCount != null;
  const metricsLabel = hasMatchedCount
    ? `${matchedCount} matched`
    : descriptor.run?.rowsExtracted
    ? `${descriptor.run.rowsExtracted} metrics`
    : null;

  return (
    <Box py={2} px={1} borderBottom="1px solid" borderColor="whiteAlpha.300">
      <HStack spacing={2} align="center">
        <Box flexShrink={0}>
          <Icon size={22} color="white" />
        </Box>
        <FileDownloadButton
          fileInfo={fileInfo}
          as={Text}
          fontSize="body.xs"
          color="white"
          flex={1}
          noOfLines={1}
        >
          {fileInfo.title || fileInfo.filename || fileInfo.name || fileInfo.id}
        </FileDownloadButton>

        {state === SOURCE_STATE.READY && metricsLabel ? (
          <Text fontSize="body.xxs" color="whiteAlpha.800" flexShrink={0}>
            {metricsLabel}
          </Text>
        ) : null}

        {/* Hidden rather than disabled without write access: an authority denial answers 401 here, so a
            live-looking control a viewer cannot use is a dead end. */}
        {canEdit && action ? (
          <SourceAction
            action={action}
            taskId={taskId}
            isStarting={isStarting}
            isBlocked={isBlocked}
            onExtract={onExtract}
            onReview={onReview}
            onCancel={onCancel}
          />
        ) : null}

        {canEdit ? (
          <Tooltip
            label="Remove from this workstream's sources. Anything already extracted or approved is kept."
            openDelay={400}
          >
            <Box
              as="button"
              type="button"
              flexShrink={0}
              onClick={onRemove}
              aria-label="Remove source"
              color="white"
              _hover={{ color: "red.300" }}
            >
              <X size={16} />
            </Box>
          </Tooltip>
        ) : null}
      </HStack>

      {note ? (
        <Text fontSize="body.xxs" color="whiteAlpha.800" pl="30px" pt={1}>
          {note}
        </Text>
      ) : null}
    </Box>
  );
}

function SourceAction({ action, taskId, isStarting, isBlocked, onExtract, onReview, onCancel }) {
  if (action === "cancel") {
    return (
      <HStack spacing={2} flexShrink={0}>
        {/* Only once there is a real id — AIProgressWidget's `enabled` guard reads an undefined id as
            enabled, which produced GET /v0/task/undefined. */}
        {taskId ? (
          <AIProgressWidget taskId={taskId} />
        ) : (
          <Text fontSize="body.xxs" color="whiteAlpha.800">
            Starting…
          </Text>
        )}
        <Button size="xs" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </HStack>
    );
  }

  if (action === "review") {
    return (
      <Button
        size="xs"
        variant="primary"
        flexShrink={0}
        leftIcon={<MagnifyingGlass size={12} />}
        onClick={onReview}
      >
        Review
      </Button>
    );
  }

  const label = action === "retry" ? "Retry" : "Review";
  return (
    <Tooltip
      label={isBlocked ? "Another document is being read — wait for it to finish." : undefined}
      openDelay={300}
    >
      <Button
        size="xs"
        variant="primary"
        flexShrink={0}
        leftIcon={<Sparkle size={12} />}
        isLoading={isStarting}
        isDisabled={isBlocked}
        onClick={onExtract}
      >
        {label}
      </Button>
    </Tooltip>
  );
}
